import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAzureDevOps } from '../services/azureDevOpsService';
import ActivityBreakdownChart from './ActivityBreakdownChart';
import {
  Activity,
  Clock,
  CheckCircle,
  AlertCircle,
  Calendar,
  BarChart3,
  RefreshCw,
  Settings,
  Loader
} from 'lucide-react';

interface DevOpsCapacityCardProps {
  userEmail: string;
  userName: string;
}

const COLORS = ['#2563EB', '#16A34A', '#CA8A04', '#DC2626', '#737373', '#8B5CF6', '#EC4899'];

const DevOpsCapacityCard: React.FC<DevOpsCapacityCardProps> = ({
  userEmail,
  userName
}) => {
  const [capacityData, setCapacityData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(false);
  const isLoadingRef = useRef(false);
  const { isConfigured, getConfigStatus, getUserCapacityData, refreshUserData } = useAzureDevOps();

  const fetchCapacityData = useCallback(async (forceRefresh = false) => {
    // Evitar múltiples cargas simultáneas
    if (isLoadingRef.current) return;

    isLoadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      console.log(`🔄 DevOpsCapacityCard - Solicitando datos para ${userEmail}`);
      const data = forceRefresh
        ? await refreshUserData(userEmail)
        : await getUserCapacityData(userEmail);

      setCapacityData(data);

      if (!data) {
        setError('Usuario no encontrado en equipos de Azure DevOps');
        console.log(`❌ DevOpsCapacityCard - Sin datos para ${userEmail}`);
      } else {
        console.log(`✅ DevOpsCapacityCard - Datos recibidos para ${userEmail}`);
      }
    } catch (err: any) {
      setError(err.message || 'Error al obtener datos de Azure DevOps');
      console.error('DevOps capacity error:', err);
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
    }
  }, [userEmail, getUserCapacityData, refreshUserData]);

  useEffect(() => {
    if (!mountedRef.current && isConfigured() && userEmail) {
      mountedRef.current = true;
      fetchCapacityData();
    }
  }, [userEmail, isConfigured, fetchCapacityData]);

  // Cleanup en unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const getUtilizationColor = (percent: number) => {
    if (percent < 70) return 'text-green-600 bg-green-100';
    if (percent < 90) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getUtilizationStatus = (percent: number) => {
    if (percent < 70) return 'Disponible';
    if (percent < 90) return 'Ocupado';
    return 'Sobrecargado';
  };

  if (!isConfigured()) {
    const configStatus = getConfigStatus();
    
    return (
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <div className="flex items-center">
          <Settings className="text-orange-600 mr-2" size={20} />
          <div>
            <h4 className="text-sm font-semibold text-orange-900">Azure DevOps</h4>
            <p className="text-xs text-orange-700">{configStatus.message}</p>
            <p className="text-xs text-orange-600 mt-1">
              Contacta al administrador para configurar las variables de entorno
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-center">
          <Loader className="animate-spin text-gray-400 mr-2" size={16} />
          <span className="text-sm text-gray-600">Cargando capacidad...</span>
        </div>
      </div>
    );
  }

  if (error || !capacityData) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="text-gray-400 mr-2" size={16} />
            <span className="text-xs text-gray-600">
              {error || 'Sin datos de DevOps'}
            </span>
          </div>
          <button
            onClick={() => fetchCapacityData(true)}
            className="p-1 text-gray-400 hover:text-gray-600"
            title="Reintentar"
          >
            <RefreshCw size={14} />
          </button>
        </div>
      </div>
    );
  }

  const { metrics, iteration, teamName } = capacityData;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <BarChart3 className="text-blue-600 mr-2" size={16} />
          <div>
            <h4 className="text-sm font-semibold text-gray-900">Azure DevOps</h4>
            <p className="text-xs text-gray-600">
              {teamName && <span className="font-medium">{teamName}</span>}
              {teamName && iteration?.name && ' • '}
              {iteration?.name || 'Sprint actual'}
            </p>
          </div>
        </div>
        <button
          onClick={() => fetchCapacityData(true)}
          className="p-1 text-gray-400 hover:text-gray-600"
          title="Actualizar"
        >
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Utilization Status */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-600">Estado:</span>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getUtilizationColor(metrics.utilizationPercent)}`}>
          {getUtilizationStatus(metrics.utilizationPercent)} ({metrics.utilizationPercent.toFixed(0)}%)
        </span>
      </div>

      {/* Capacity Progress Bars */}
      <div className="space-y-3">
        {/* Sprint completo */}
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-gray-600">
            <span>Capacidad total del sprint</span>
            <span>{metrics.utilizationPercent.toFixed(1)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${
                metrics.utilizationPercent < 70 ? 'bg-green-500' :
                metrics.utilizationPercent < 90 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${Math.min(metrics.utilizationPercent, 100)}%` }}
            ></div>
          </div>
        </div>

        {/* Tiempo restante */}
        {metrics.remainingWorkDays > 0 && (
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-gray-600">
              <span>Trabajo pendiente vs tiempo restante</span>
              <span className={`font-medium ${
                metrics.remainingUtilizationPercent < 70 ? 'text-green-600' :
                metrics.remainingUtilizationPercent < 90 ? 'text-orange-600' : 'text-red-600'
              }`}>
                {metrics.remainingUtilizationPercent.toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${
                  metrics.remainingUtilizationPercent < 70 ? 'bg-blue-500' :
                  metrics.remainingUtilizationPercent < 90 ? 'bg-orange-500' : 'bg-red-500'
                }`}
                style={{ width: `${Math.min(metrics.remainingUtilizationPercent, 100)}%` }}
              ></div>
            </div>
            {metrics.remainingUtilizationPercent > 100 && (
              <div className="text-xs text-red-600 font-medium">
                ⚠️ Sobrecarga: Se necesitan {(metrics.remainingWork / metrics.totalCapacityPerDay).toFixed(1)} días adicionales
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sprint Info */}
      <div className="text-xs text-gray-600 text-center bg-gray-50 py-2 px-3 rounded">
        <div className="flex items-center justify-between">
          <span className="font-medium">Sprint de {metrics.sprintWorkDays} días</span>
          <span className={`font-medium ${
            metrics.remainingWorkDays <= 1 ? 'text-red-600' : 
            metrics.remainingWorkDays <= 2 ? 'text-orange-600' : 'text-blue-600'
          }`}>
            {metrics.remainingWorkDays} días restantes
          </span>
        </div>
        {metrics.availableWorkDays !== metrics.remainingWorkDays && (
          <div className="text-orange-600 mt-1">
            {metrics.availableWorkDays} días disponibles (con días libres)
          </div>
        )}
        <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
          <div>
            <span className="text-gray-500">Capacity total:</span>
            <span className="font-medium text-gray-800 ml-1">{metrics.totalSprintCapacity}h</span>
          </div>
          <div>
            <span className="text-gray-500">Capacity restante:</span>
            <span className="font-medium text-blue-600 ml-1">{metrics.remainingSprintCapacity}h</span>
          </div>
        </div>
      </div>

      {/* Capacity Details */}
      <div className="grid grid-cols-2 gap-3 text-xs">
        <div className="flex items-center">
          <Clock className="text-gray-400 mr-1" size={12} />
          <span className="text-gray-600">
            {metrics.totalCapacityPerDay}h/día
          </span>
        </div>
        <div className="flex items-center">
          <Activity className="text-blue-500 mr-1" size={12} />
          <span className="text-gray-600">
            {metrics.workItemsCount} items
          </span>
        </div>
        <div className="flex items-center">
          <CheckCircle className="text-green-500 mr-1" size={12} />
          <span className="text-gray-600">
            {metrics.completedWork}h hechas
          </span>
        </div>
        <div className="flex items-center">
          <AlertCircle className="text-orange-500 mr-1" size={12} />
          <span className="text-gray-600">
            {metrics.remainingWork}h pendientes
          </span>
        </div>
      </div>

      {/* Activities Breakdown - VERSIÓN CON GRÁFICO */}
      {metrics.activities.length > 0 && (
        <div className="border-t border-neutral-200 dark:border-neutral-700 pt-3">
          <h5 className="text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-3 flex items-center">
            <BarChart3 className="mr-1" size={14} />
            Distribución de Actividades ({metrics.activities.length} actividad{metrics.activities.length > 1 ? 'es' : ''})
          </h5>

          {/* PieChart si hay múltiples actividades */}
          {metrics.activities.length > 1 ? (
            <ActivityBreakdownChart
              activities={metrics.activities}
              totalCapacity={metrics.totalCapacityPerDay}
            />
          ) : (
            // Barra simple para 1 actividad
            <div className="space-y-2">
              {metrics.activities.map((activity: any, index: number) => (
                <div key={index} className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-neutral-600 dark:text-neutral-400">{activity.name}</span>
                    <span className="text-neutral-800 dark:text-neutral-200 font-medium">
                      {activity.capacityPerDay}h/día (100%)
                    </span>
                  </div>
                  <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2">
                    <div className="bg-primary h-2 rounded-full" style={{ width: '100%' }}></div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Lista compacta debajo del gráfico */}
          {metrics.activities.length > 1 && (
            <div className="mt-3 space-y-1">
              {metrics.activities.map((activity: any, index: number) => (
                <div key={index} className="flex items-center justify-between text-xs">
                  <div className="flex items-center">
                    <div
                      className="w-2 h-2 rounded-full mr-2"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <span className="text-neutral-600 dark:text-neutral-400">{activity.name}</span>
                  </div>
                  <span className="text-neutral-800 dark:text-neutral-200 font-medium">
                    {activity.capacityPerDay}h/día
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Days Off */}
      {metrics.daysOff.length > 0 && (
        <div className="border-t pt-2">
          <div className="flex items-center justify-between text-xs text-orange-600">
            <div className="flex items-center">
              <Calendar className="mr-1" size={12} />
              <span>{metrics.daysOff.length} día(s) libre(s)</span>
            </div>
            <span className="text-orange-800 font-medium">
              {metrics.availableWorkDays}/{metrics.sprintWorkDays} días disponibles
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(DevOpsCapacityCard);